<app-loading *ngIf="contentReady"></app-loading>

<div class="bb-page" *ngIf="!contentReady">
  <div class="bb-page-shell">

    <!-- Header -->
    <div class="bb-page-header">
      <div class="bb-page-title-block">
        <span class="bb-eyebrow">{{ 'Vaccination Records' | transloco }}</span>
        <h2 class="bb-page-title">{{ 'Vaccination Records' | transloco }}</h2>
        <p class="bb-page-subtitle">{{ 'Vaccination History' | transloco }}</p>
      </div>
    </div>

    <!-- Vaccination Form -->
    <div class="bb-card bb-p-20 bb-mb-16">
      <form [formGroup]="vaccinationForm" (ngSubmit)="saveVaccination()">
        <div class="bb-form-grid">

          @if(submitted && vaccinationForm.invalid) {
          <div class="bb-form-alert bb-field-full" role="alert">
            {{ 'Review the required fields before saving' | transloco }}

            @if(getMissingRequiredCount() > 0) {
            <span>
              ({{ getMissingRequiredCount() }} {{ 'required fields' | transloco }})
            </span>
            }
          </div>
          }

          <!-- Vaccine Name -->
          <div class="bb-field">
            <label class="bb-label" for="vaccine_name">{{ 'vaccine_name' | transloco }}</label>
            <input
              class="bb-input"
              id="vaccine_name"
              type="text"
              formControlName="vaccine_name"
              placeholder="{{ 'vaccine_name' | transloco }}"
              [ngClass]="{
                'is-invalid': vaccinationForm.get('vaccine_name')?.touched && vaccinationForm.get('vaccine_name')?.invalid,
                'is-valid': vaccinationForm.get('vaccine_name')?.touched && vaccinationForm.get('vaccine_name')?.valid
              }"
            >

            @if(shouldShowError('vaccine_name')) {
            <div class="bb-field-error">
              {{ getErrorMessage('vaccine_name', ('vaccine_name' | transloco)) }}
            </div>
            }
          </div>

          <!-- Manufacturer -->
          <div class="bb-field">
            <label class="bb-label" for="manufacturer">{{ 'manufacturer' | transloco }}</label>
            <input
              class="bb-input"
              id="manufacturer"
              type="text"
              formControlName="manufacturer"
              placeholder="{{ 'manufacturer' | transloco }}"
              [ngClass]="{
                'is-invalid': vaccinationForm.get('manufacturer')?.touched && vaccinationForm.get('manufacturer')?.invalid,
                'is-valid': vaccinationForm.get('manufacturer')?.touched && vaccinationForm.get('manufacturer')?.valid
              }"
            >
          </div>

          <!-- Batch Number -->
          <div class="bb-field">
            <label class="bb-label" for="batch_number">{{ 'batch_number' | transloco }}</label>
            <input
              class="bb-input"
              id="batch_number"
              type="text"
              formControlName="batch_number"
              placeholder="{{ 'batch_number' | transloco }}"
              [ngClass]="{
                'is-invalid': vaccinationForm.get('batch_number')?.touched && vaccinationForm.get('batch_number')?.invalid,
                'is-valid': vaccinationForm.get('batch_number')?.touched && vaccinationForm.get('batch_number')?.valid
              }"
            >
          </div>

          <!-- Vaccination Date -->
          <div class="bb-field">
            <label class="bb-label" for="vaccination_date">{{ 'vaccination_date' | transloco }}</label>

            <div class="bb-booking-calendar-field" [dir]="isAr ? 'rtl' : 'ltr'">
              <button
                type="button"
                class="bb-booking-calendar-trigger"
                id="vaccination_date"
                (click)="toggleDateCalendar('vaccination_date')"
                [ngClass]="{
                  'is-invalid': vaccinationForm.get('vaccination_date')?.touched && vaccinationForm.get('vaccination_date')?.invalid,
                  'is-valid': vaccinationForm.get('vaccination_date')?.touched && vaccinationForm.get('vaccination_date')?.valid
                }"
              >
                <span>
                  {{ getDateDisplay('vaccination_date', ('Select vaccination date' | transloco)) }}
                </span>
                <i class="fa-solid fa-calendar-days"></i>
              </button>

              @if(calendarState.vaccination_date.show) {
              <div class="bb-booking-calendar-panel">
                <div class="bb-booking-calendar-head">
                  <button type="button" (click)="previousDateCalendarMonth('vaccination_date')">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
                  </button>

                  <strong>{{ getDateCalendarTitle('vaccination_date') }}</strong>

                  <button type="button" (click)="nextDateCalendarMonth('vaccination_date')">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-left' : 'fa-chevron-right'"></i>
                  </button>
                </div>

                <div class="bb-booking-calendar-weekdays">
                  @for(dayName of getDateCalendarWeekDays(); track dayName) {
                  <span>{{ dayName }}</span>
                  }
                </div>

                <div class="bb-booking-calendar-grid">
                  @for(day of getDateCalendarDays('vaccination_date'); track day.date.getTime()) {
                  <button
                    type="button"
                    class="bb-booking-calendar-day"
                    [class.other-month]="!day.isCurrentMonth"
                    [class.today]="day.isToday"
                    [class.selected]="day.isSelected"
                    [disabled]="day.isDisabled"
                    (click)="selectDateCalendarDate('vaccination_date', day.date)"
                  >
                    {{ day.dayNumber }}
                  </button>
                  }
                </div>
              </div>
              }
            </div>

            <input type="hidden" formControlName="vaccination_date">

            @if(shouldShowError('vaccination_date')) {
            <div class="bb-field-error">
              {{ getErrorMessage('vaccination_date', ('vaccination_date' | transloco)) }}
            </div>
            }
          </div>

          <!-- Next Due Date -->
          <div class="bb-field">
            <label class="bb-label" for="next_due_date">{{ 'next_due_date' | transloco }}</label>

            <div class="bb-booking-calendar-field" [dir]="isAr ? 'rtl' : 'ltr'">
              <button
                type="button"
                class="bb-booking-calendar-trigger"
                id="next_due_date"
                (click)="toggleDateCalendar('next_due_date')"
                [ngClass]="{
                  'is-invalid': vaccinationForm.get('next_due_date')?.touched && vaccinationForm.get('next_due_date')?.invalid,
                  'is-valid': vaccinationForm.get('next_due_date')?.touched && vaccinationForm.get('next_due_date')?.value
                }"
              >
                <span>
                  {{ getDateDisplay('next_due_date', ('Select next due date' | transloco)) }}
                </span>
                <i class="fa-solid fa-calendar-days"></i>
              </button>

              @if(calendarState.next_due_date.show) {
              <div class="bb-booking-calendar-panel">
                <div class="bb-booking-calendar-head">
                  <button type="button" (click)="previousDateCalendarMonth('next_due_date')">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
                  </button>

                  <strong>{{ getDateCalendarTitle('next_due_date') }}</strong>

                  <button type="button" (click)="nextDateCalendarMonth('next_due_date')">
                    <i class="fa-solid" [ngClass]="isAr ? 'fa-chevron-left' : 'fa-chevron-right'"></i>
                  </button>
                </div>

                <div class="bb-booking-calendar-weekdays">
                  @for(dayName of getDateCalendarWeekDays(); track dayName) {
                  <span>{{ dayName }}</span>
                  }
                </div>

                <div class="bb-booking-calendar-grid">
                  @for(day of getDateCalendarDays('next_due_date'); track day.date.getTime()) {
                  <button
                    type="button"
                    class="bb-booking-calendar-day"
                    [class.other-month]="!day.isCurrentMonth"
                    [class.today]="day.isToday"
                    [class.selected]="day.isSelected"
                    [disabled]="day.isDisabled"
                    (click)="selectDateCalendarDate('next_due_date', day.date)"
                  >
                    {{ day.dayNumber }}
                  </button>
                  }
                </div>
              </div>
              }
            </div>

            <input type="hidden" formControlName="next_due_date">
          </div>

          <!-- Authorized Vet -->
          <div class="bb-field">
            <label class="bb-label" for="authorized_vet">{{ 'authorized_vet' | transloco }}</label>
            <input
              class="bb-input"
              id="authorized_vet"
              type="text"
              formControlName="authorized_vet"
              placeholder="{{ 'authorized_vet' | transloco }}"
              [ngClass]="{
                'is-invalid': vaccinationForm.get('authorized_vet')?.touched && vaccinationForm.get('authorized_vet')?.invalid,
                'is-valid': vaccinationForm.get('authorized_vet')?.touched && vaccinationForm.get('authorized_vet')?.valid
              }"
            >
          </div>

          <!-- Notes -->
          <div class="bb-field bb-field-full">
            <label class="bb-label" for="notes">{{ 'note' | transloco }}</label>
            <textarea
              class="bb-input bb-textarea"
              id="notes"
              rows="3"
              formControlName="notes"
              placeholder="{{ 'note' | transloco }}"
              [ngClass]="{
                'is-invalid': vaccinationForm.get('notes')?.touched && vaccinationForm.get('notes')?.invalid,
                'is-valid': vaccinationForm.get('notes')?.touched && vaccinationForm.get('notes')?.valid
              }"
            ></textarea>
          </div>

          <!-- Submit & Reset Buttons -->
          <div class="bb-field bb-field-full">
            <div class="bb-flex bb-gap-12 bb-wrap bb-justify-end">
              <button
                class="bb-btn bb-btn-primary bb-btn-wide"
                type="submit"
                [class.is-disabled]="vaccinationForm.invalid"
              >
                {{ editMode ? (('Update' | transloco)) : ("Submit" | transloco) }}
              </button>

              <button
                class="bb-btn bb-btn-secondary bb-btn-wide"
                type="button"
                (click)="resetForm()"
              >
                {{ 'Reset' | transloco }}
              </button>
            </div>
          </div>

        </div>
      </form>
    </div>

    <!-- Vaccinations Table -->
    <div class="bb-table-card bb-p-16 bb-mt-20">
      <div class="bb-flex bb-items-center bb-justify-between bb-gap-12 bb-wrap bb-mb-12">
        <label class="bb-label bb-m-0">{{ 'Vaccination History' | transloco }}</label>
      </div>

      <div class="bb-table-scroll" *ngIf="!noData">
        <table class="bb-table table-custom">
          <thead>
            <tr>
              <th class="mat-header-cell-custom">{{ 'vaccine_name' | transloco }}</th>
              <th class="mat-header-cell-custom">{{ 'manufacturer' | transloco }}</th>
              <th class="mat-header-cell-custom">{{ 'batch_number' | transloco }}</th>
              <th class="mat-header-cell-custom">{{ 'vaccination_date' | transloco }}</th>
              <th class="mat-header-cell-custom">{{ 'next_due_date' | transloco }}</th>
              <th class="mat-header-cell-custom">{{ 'authorized_vet' | transloco }}</th>
              <th class="mat-header-cell-custom">{{ 'note' | transloco }}</th>
              <th class="mat-header-cell-custom bb-actions-th">{{ 'Actions' | transloco }}</th>
            </tr>
          </thead>

          <tbody>
            <tr class="mat-row-custom" *ngFor="let element of dataSource.data">

              <!-- Vaccine Name -->
              <td class="mat-cell-custom">
                <strong class="bb-product-title">{{ element.vaccine_name || '—' }}</strong>
              </td>

              <!-- Manufacturer -->
              <td class="mat-cell-custom">{{ element.manufacturer || '—' }}</td>

              <!-- Batch Number -->
              <td class="mat-cell-custom">
                <span class="bb-badge bb-badge-soft-blue">{{ element.batch_number || '—' }}</span>
              </td>

              <!-- Vaccination Date -->
              <td class="mat-cell-custom">
                {{ element.vaccination_date ? (element.vaccination_date | date: 'yyyy-MM-dd') : '—' }}
              </td>

              <!-- Next Due Date -->
              <td class="mat-cell-custom">
                <span
                  class="bb-font-semibold"
                  [class.bb-text-warning]="isUpcoming(element.next_due_date)"
                  [class.bb-text-danger]="isOverdue(element.next_due_date)"
                >
                  {{ element.next_due_date ? (element.next_due_date | date: 'yyyy-MM-dd') : '—' }}
                </span>
              </td>

              <!-- Authorized Vet -->
              <td class="mat-cell-custom">{{ element.authorized_vet || '—' }}</td>

              <!-- Notes -->
              <td class="mat-cell-custom">
                <span class="bb-truncate bb-block" [title]="element.notes">
                  {{ element.notes || '—' }}
                </span>
              </td>

              <!-- Actions -->
              <td class="mat-cell-custom bb-actions-td">
                <div class="bb-actions-cell">
                  <button
                    class="bb-icon-btn"
                    type="button"
                    (click)="editVaccination(element)"
                    title="{{ 'Edit' | transloco }}"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>

                  <button
                    class="bb-icon-btn bb-icon-btn-danger"
                    type="button"
                    (click)="deleteVaccination(element.id)"
                    title="{{ 'Delete' | transloco }}"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              </td>

            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Data State -->
      <div *ngIf="noData" class="bb-empty-state bb-text-center">
        <app-nodata></app-nodata>
      </div>
    </div>

  </div>
</div>